import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { z } from "zod";
import { Calculator, ArrowRight, Mail, Check, Loader2, FileDown } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { buildSeoMeta } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";


export const Route = createFileRoute("/kalkulator")({
  head: () =>
    buildSeoMeta({
      title: "Kalkulator wyceny wdrożenia e-commerce | SyncTech",
      description:
        "Sprawdź widełkową wycenę wdrożenia Baselinker, Shopify, Shoper lub innej platformy w 2 minuty. Bez rejestracji. Otrzymaj szczegółową ofertę PDF na e-mail.",
      path: "/kalkulator",
    }),
  component: KalkulatorPage,
});

// ---- Konfiguracja wyceny (fallback do czasu załadowania z bazy) ----

type Platform = { id: string; label: string; base: number };
type OrderRange = { id: string; label: string; multiplier: number };
type Integration = { id: string; label: string; cost: number };

const DEFAULT_PLATFORMS: Platform[] = [
  { id: "shopify", label: "Shopify", base: 4500 },
  { id: "shoper", label: "Shoper", base: 3500 },
  { id: "shopgold", label: "ShopGold", base: 3200 },
  { id: "prestashop", label: "PrestaShop", base: 5500 },
  { id: "other", label: "Inna / nie wiem jeszcze", base: 3800 },
  { id: "have", label: "Mam już platformę (nie wdrażamy)", base: 0 },
];

const DEFAULT_RANGES: OrderRange[] = [
  { id: "small", label: "Do 500 / mies.", multiplier: 1.0 },
  { id: "medium", label: "500–2 000 / mies.", multiplier: 1.15 },
  { id: "large", label: "2 000–5 000 / mies.", multiplier: 1.35 },
  { id: "xlarge", label: "5 000+ / mies.", multiplier: 1.65 },
];

const DEFAULT_INTEGRATIONS: Integration[] = [
  { id: "baselinker", label: "Baselinker (multichannel)", cost: 1800 },
  { id: "sellasist", label: "SellAsist (multichannel)", cost: 1600 },
  { id: "allegro", label: "Allegro", cost: 900 },
  { id: "amazon", label: "Amazon / eBay", cost: 1500 },
  { id: "couriers", label: "Kurierzy (InPost, DPD, DHL)", cost: 800 },
  { id: "erp", label: "ERP (Subiekt, Comarch, własny)", cost: 2200 },
  { id: "ads", label: "Google Ads + GA4 + CAPI", cost: 1400 },
  { id: "email", label: "Email automation (Klaviyo, Mailchimp)", cost: 1000 },
  { id: "b2b", label: "Panel B2B / hurtownie", cost: 2800 },
  { id: "migration", label: "Migracja z innego sklepu", cost: 1700 },
];

function calcPrice(
  platforms: Platform[],
  ranges: OrderRange[],
  integrations: Integration[],
  platform: string,
  range: string,
  picked: string[],
) {
  const p = platforms.find((x) => x.id === platform);
  const r = ranges.find((x) => x.id === range);
  if (!p || !r) return { low: 0, high: 0 };
  const integrationsSum = picked.reduce((acc, id) => {
    const i = integrations.find((x) => x.id === id);
    return acc + (i?.cost ?? 0);
  }, 0);
  const base = (p.base + integrationsSum) * r.multiplier;
  return {
    low: Math.round((base * 0.92) / 100) * 100,
    high: Math.round((base * 1.18) / 100) * 100,
  };
}


const emailSchema = z
  .string()
  .trim()
  .min(1, "Podaj e-mail")
  .email("Niepoprawny e-mail")
  .max(255);

function KalkulatorPage() {
  const [platforms, setPlatforms] = useState<Platform[]>(DEFAULT_PLATFORMS);
  const [orderRanges, setOrderRanges] = useState<OrderRange[]>(DEFAULT_RANGES);
  const [integrations, setIntegrations] = useState<Integration[]>(DEFAULT_INTEGRATIONS);

  const [platform, setPlatform] = useState<string>("shopify");
  const [range, setRange] = useState<string>("medium");
  const [picked, setPicked] = useState<string[]>(["baselinker", "couriers"]);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [p, r, i] = await Promise.all([
        supabase.from("pricing_platforms").select("slug,label,base,sort_order,active").eq("active", true).order("sort_order"),
        supabase.from("pricing_ranges").select("slug,label,multiplier,sort_order,active").eq("active", true).order("sort_order"),
        supabase.from("pricing_integrations").select("slug,label,cost,sort_order,active").eq("active", true).order("sort_order"),
      ]);
      if (cancelled) return;
      if (p.data?.length) setPlatforms(p.data.map((x) => ({ id: x.slug, label: x.label, base: Number(x.base) })));
      if (r.data?.length) setOrderRanges(r.data.map((x) => ({ id: x.slug, label: x.label, multiplier: Number(x.multiplier) })));
      if (i.data?.length) setIntegrations(i.data.map((x) => ({ id: x.slug, label: x.label, cost: Number(x.cost) })));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const price = useMemo(
    () => calcPrice(platforms, orderRanges, integrations, platform, range, picked),
    [platforms, orderRanges, integrations, platform, range, picked],
  );

  function toggleIntegration(id: string) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }


  async function sendOffer(e: React.FormEvent) {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0].message);
      return;
    }
    setEmailError(null);
    setSending(true);
    try {
      // Symulacja — docelowo: zapis do Lovable Cloud + transactional email z PDF
      await new Promise((r) => setTimeout(r, 800));
      setSent(true);
      toast.success("Oferta wysłana", {
        description: "Sprawdź skrzynkę — szczegółowa rozpiska dotrze w ciągu 5 minut.",
      });
      setTimeout(() => {
        navigate({ to: "/dziekujemy", search: { source: "kalkulator" } });
      }, 1000);
    } catch {
      toast.error("Nie udało się wysłać", {
        description: "Spróbuj ponownie lub napisz na kontakt@synctech.pl",
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="pt-32 pb-24">
        {/* Hero */}
        <section className="mx-auto max-w-4xl px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-primary"
          >
            <Calculator className="h-3.5 w-3.5" /> Kalkulator wyceny
          </motion.div>
          <h1 className="font-display text-4xl font-bold md:text-5xl">
            Sprawdź widełki w <span className="text-gradient">2 minuty</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Wybierz platformę, skalę sklepu i potrzebne integracje — od razu zobaczysz orientacyjną
            cenę. Bez rejestracji.
          </p>
        </section>

        {/* Kalkulator */}
        <section className="mx-auto mt-12 max-w-6xl px-6">
          <div className="grid gap-8 lg:grid-cols-[1fr,420px]">
            {/* Lewa: pytania */}
            <div className="space-y-8 rounded-3xl border border-border/60 bg-card/50 p-8 backdrop-blur-sm">
              {/* Platforma */}
              <div>
                <Label className="text-base font-semibold">1. Twoja platforma sklepowa</Label>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {platforms.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPlatform(p.id)}
                      className={`rounded-xl border px-4 py-3 text-left text-sm transition ${
                        platform === p.id
                          ? "border-primary bg-primary/10 text-foreground shadow-[var(--shadow-glow)]"
                          : "border-border/60 bg-muted/20 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{p.label}</span>
                        {platform === p.id && <Check className="h-4 w-4 text-primary" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Skala */}
              <div>
                <Label className="text-base font-semibold">2. Liczba zamówień miesięcznie</Label>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {orderRanges.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setRange(r.id)}
                      className={`rounded-xl border px-4 py-3 text-left text-sm transition ${
                        range === r.id
                          ? "border-primary bg-primary/10 text-foreground shadow-[var(--shadow-glow)]"
                          : "border-border/60 bg-muted/20 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{r.label}</span>
                        {range === r.id && <Check className="h-4 w-4 text-primary" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Integracje */}
              <div>
                <Label className="text-base font-semibold">
                  3. Potrzebne integracje{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    (wybierz dowolną liczbę)
                  </span>
                </Label>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {integrations.map((i) => {
                    const active = picked.includes(i.id);
                    return (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => toggleIntegration(i.id)}
                        className={`rounded-xl border px-4 py-3 text-left text-sm transition ${
                          active
                            ? "border-primary bg-primary/10 text-foreground shadow-[var(--shadow-glow)]"
                            : "border-border/60 bg-muted/20 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{i.label}</span>
                          {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Prawa: wynik */}
            <aside className="lg:sticky lg:top-28 lg:h-fit">
              <motion.div
                key={`${platform}-${range}-${picked.join(",")}`}
                initial={{ opacity: 0.6, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden rounded-3xl border border-primary/40 bg-gradient-to-br from-primary/15 via-card/60 to-card/40 p-8 backdrop-blur-md shadow-[var(--shadow-glow)]"
              >
                <p className="text-xs font-medium uppercase tracking-wider text-primary">
                  Orientacyjna wycena
                </p>
                <div className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">
                  <span className="text-gradient">
                    {price.low.toLocaleString("pl-PL")} – {price.high.toLocaleString("pl-PL")} zł
                  </span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  netto, jednorazowo.
                </p>

                <div className="mt-6 space-y-2 border-t border-border/40 pt-5 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground">Platforma:</span>
                    <span className="font-medium">
                      {platforms.find((p) => p.id === platform)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground">Skala:</span>
                    <span className="font-medium">
                      {orderRanges.find((r) => r.id === range)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted-foreground">Integracje:</span>
                    <span className="font-medium">{picked.length}</span>
                  </div>
                </div>

                <Button asChild size="lg" className="mt-6 w-full shadow-[var(--shadow-glow)]">
                  <a href="/#kontakt">
                    Umów konsultację <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>

                {/* Lead capture — opcjonalny e-mail po szczegóły */}
                {!sent ? (
                  <form onSubmit={sendOffer} className="mt-6 border-t border-border/40 pt-5">
                    <div className="mb-2 flex items-start gap-2 text-sm">
                      <FileDown className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        Chcesz <strong>szczegółową rozpiskę PDF</strong> z timeline i zakresem prac?
                      </span>
                    </div>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError(null);
                        }}
                        placeholder="twoj@email.pl"
                        className="flex-1"
                        aria-invalid={!!emailError}
                      />
                      <Button type="submit" disabled={sending} variant="outline" size="default">
                        {sending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Mail className="mr-1.5 h-4 w-4" /> Wyślij
                          </>
                        )}
                      </Button>
                    </div>
                    {emailError && (
                      <p className="mt-1.5 text-xs font-medium text-destructive">{emailError}</p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      Tylko wycena — żadnego spamu. RODO zgodnie z polityką.
                    </p>
                  </form>
                ) : (
                  <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-primary/40 bg-primary/10 p-4 text-sm">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <div>
                      <p className="font-semibold">Oferta wysłana!</p>
                      <p className="mt-1 text-muted-foreground">
                        Sprawdź skrzynkę — szczegółowa rozpiska dotrze w ciągu 5 minut.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            </aside>
          </div>
        </section>

        {/* Podsumowanie wyceny */}
        <section className="mx-auto mt-16 max-w-4xl px-6">
          <motion.div
            key={`summary-${platform}-${range}-${picked.join(",")}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden rounded-3xl border border-border/60 bg-card/50 p-8 backdrop-blur-sm"
          >
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary">
              <FileDown className="h-3.5 w-3.5" /> Podsumowanie wyceny
            </div>
            <h2 className="mt-2 font-display text-2xl font-bold md:text-3xl">
              Co składa się na Twoją wycenę
            </h2>

            {(() => {
              const platformObj = platforms.find((p) => p.id === platform)!;
              const rangeObj = orderRanges.find((r) => r.id === range)!;
              const pickedItems = picked
                .map((id) => integrations.find((i) => i.id === id))
                .filter((x): x is Integration => Boolean(x));
              const integrationsSum = pickedItems.reduce((acc, i) => acc + i.cost, 0);
              const subtotal = platformObj.base + integrationsSum;
              const total = Math.round((subtotal * rangeObj.multiplier) / 100) * 100;

              return (
                <div className="mt-6 space-y-6">
                  {/* Platforma */}
                  <div className="rounded-2xl border border-border/40 bg-muted/10 p-5">
                    <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Platforma sklepowa
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-semibold">{platformObj.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {platformObj.base === 0
                            ? "Pomijamy wdrożenie — pracujemy na Twojej istniejącej platformie"
                            : "Bazowe wdrożenie + konfiguracja"}
                        </div>
                      </div>
                      <div className="font-display text-lg font-bold">
                        {platformObj.base === 0
                          ? "0 zł"
                          : `${platformObj.base.toLocaleString("pl-PL")} zł`}
                      </div>
                    </div>
                  </div>

                  {/* Integracje */}
                  <div className="rounded-2xl border border-border/40 bg-muted/10 p-5">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Wybrane integracje ({pickedItems.length})
                      </div>
                      <div className="text-xs text-muted-foreground">
                        suma: {integrationsSum.toLocaleString("pl-PL")} zł
                      </div>
                    </div>
                    {pickedItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Nie wybrano żadnych integracji. Wróć do listy powyżej i zaznacz np.
                        Baselinker, SellAsist lub kurierów.
                      </p>
                    ) : (
                      <ul className="divide-y divide-border/40">
                        {pickedItems.map((i) => (
                          <li
                            key={i.id}
                            className="flex items-center justify-between gap-4 py-2.5 text-sm"
                          >
                            <span className="flex items-center gap-2">
                              <Check className="h-4 w-4 shrink-0 text-primary" />
                              <span className="font-medium">{i.label}</span>
                            </span>
                            <span className="font-medium tabular-nums">
                              {i.cost.toLocaleString("pl-PL")} zł
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Skala */}
                  <div className="flex items-center justify-between gap-4 rounded-2xl border border-border/40 bg-muted/10 p-5 text-sm">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Skala (mnożnik wolumenu)
                      </div>
                      <div className="mt-1 font-medium">{rangeObj.label}</div>
                    </div>
                    <div className="font-display text-lg font-bold">
                      ×{rangeObj.multiplier.toFixed(2)}
                    </div>
                  </div>

                  {/* Total */}
                  <div className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/15 via-card/60 to-card/40 p-6 shadow-[var(--shadow-glow)]">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                          Łączny koszt
                        </div>
                        <div className="mt-1 font-display text-3xl font-bold md:text-4xl">
                          od{" "}
                          <span className="text-gradient">
                            {total.toLocaleString("pl-PL")} zł
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          netto, jednorazowo · widełki: {price.low.toLocaleString("pl-PL")} –{" "}
                          {price.high.toLocaleString("pl-PL")} zł
                        </p>
                      </div>
                      <Button asChild size="lg" className="shrink-0">
                        <a href="/#kontakt">
                          Umów konsultację <ArrowRight className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </motion.div>
        </section>

        {/* Disclaimer */}
        <section className="mx-auto mt-16 max-w-3xl px-6">
          <div className="rounded-2xl border border-border/60 bg-muted/20 p-6 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Jak liczymy wycenę?</strong> Bazujemy na średnich
              z 50+ wdrożeń (2023–2025). Cena finalna zależy od stanu danych źródłowych, custom
              logiki biznesowej i wymaganego SLA — dlatego prezentujemy widełki. Po bezpłatnej
              konsultacji dostajesz wycenę z dokładnością ±5%.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button asChild size="sm" variant="outline">
                <Link to="/cennik">Zobacz pełne pakiety</Link>
              </Button>
              <Button asChild size="sm" variant="ghost">
                <a href="/#kontakt">Umów konsultację</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieBanner />
      <Toaster richColors position="top-right" />
    </div>
  );
}
